import { useEffect, useState } from 'react';
import { Card, Form, Typography, Button, Statistic, Col, InputNumber, Select, Descriptions, Tag, Table, DatePicker, Space, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { users } from '../../../../../../../API/TT_ELD/Users/user';
import { companies } from '../../../../../../../API/TT_ELD/Users/company';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useCompanyData, useFreeTrialHistory } from '../../../../../../../Hooks/TT_ELD/Companies';
import { getUserFromLocalStorage } from '../../../../../../../Utils/sidebar';
import { isWhiteLabelAdmin } from '../../../../../../../Utils/whiteLabel';
import {
  EProjectNames,
  isTestEnvProject,
  useProjectData,
} from '../../../../../../../Hooks/useProjectData';
import moment from 'moment';
import { HistoryOutlined } from '@ant-design/icons';
import FreeTrialChangesModal from './FreeTrialChangesModal';

const { Timer } = Statistic;
const { Text } = Typography;

type MyObjectType = { [key: string | number]: any };
type params = { readonly id: any };
interface FreeTrialResponse { data: any; error: string }

const CAN_SUBMIT_ROLES = ['support_manager', 'manager', 'sales'];
const EXTENDED_LIMIT_ROLES = ['manager', 'sales'];
const HISTORY_PAGE_SIZE = 10;
// A changed free trial end date can be at most this many days from today
const MAX_END_DATE_DAYS = 35;

const formatDate = (date?: string | null) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');

const historyColumns = [
  {
    title: 'No',
    dataIndex: 'no',
    key: 'no',
    width: 60,
  },
  {
    title: 'Opened Date',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: formatDate,
  },
  {
    title: 'From',
    dataIndex: 'startDate',
    key: 'startDate',
    render: formatDate,
  },
  {
    title: 'To',
    dataIndex: 'endDate',
    key: 'endDate',
    render: formatDate,
  },
  {
    title: 'Range',
    dataIndex: 'days',
    key: 'days',
    render: (days: number | null) =>
      days !== null && days !== undefined ? `${days} day${days === 1 ? '' : 's'}` : '-',
  },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    key: 'quantity',
    render: (quantity: number | null) => quantity ?? '-',
  },
  {
    title: 'Opened By',
    dataIndex: 'createdBy',
    key: 'createdBy',
    render: (createdBy: any) => {
      if (!createdBy) return '-';
      const fullName = [createdBy.first_name, createdBy.second_name].filter(Boolean).join(' ');
      return (
        <div>
          <div>{fullName || '-'}</div>
          {createdBy.email && <Text type="secondary">{createdBy.email}</Text>}
        </div>
      );
    },
  },
  {
    title: 'Role',
    dataIndex: 'createdBy',
    key: 'role',
    render: (createdBy: any) => (createdBy?.admin_type ? <Tag>{createdBy.admin_type}</Tag> : '-'),
  },
];

const FreeTrial = () => {
  const [form] = Form.useForm();
  const { id } = useParams<params>();
  const { data, isFetching, isLoading, refetch }: MyObjectType = useCompanyData(id);
  const currentRole = getUserFromLocalStorage()?.role ?? '';
  const canSubmit = CAN_SUBMIT_ROLES.includes(currentRole);
  const maxLimitDays = EXTENDED_LIMIT_ROLES.includes(currentRole) ? 30 : 10;

  const freeTrial = data?.freeTrial;
  const isFreeTrialActive = freeTrial?.status === 'active';
  const freeTrialEndDate = freeTrial?.endDate ? new Date(freeTrial.endDate) : undefined;
  const isActivated = !!freeTrial;

  // Changing the end date is Delta ELD only (plus the UAT/BTEST test ELDs): any white-label
  // admin, or a manager on the admin/support dashboard. Only while the trial is still running.
  const { projectData } = useProjectData();
  const isEndDatePlatform =
    projectData?.projectName === EProjectNames.DELTAELD ||
    isTestEnvProject(projectData?.projectName);
  const canChangeEndDate =
    isEndDatePlatform &&
    (isWhiteLabelAdmin() || currentRole === 'manager') &&
    isFreeTrialActive &&
    !!freeTrialEndDate &&
    freeTrialEndDate.getTime() > Date.now();

  const [newEndDate, setNewEndDate] = useState<Dayjs | null>(null);
  useEffect(() => {
    setNewEndDate(freeTrial?.endDate ? dayjs(freeTrial.endDate) : null);
  }, [freeTrial?.endDate]);

  const [historyPage, setHistoryPage] = useState(1);
  const [changesTrialId, setChangesTrialId] = useState<string | number | undefined>();

  const historyColumnsWithActions = [
    ...historyColumns,
    {
      title: 'Actions',
      key: 'actions',
      render: (row: any) => {
        // changesCount comes from the backend; until it's sent, show a plain History button
        const count: number | undefined = row.changesCount;
        const hasCount = count !== null && count !== undefined;
        return (
          <Button
            size="small"
            icon={<HistoryOutlined />}
            disabled={hasCount && count === 0}
            onClick={() => setChangesTrialId(row.id)}
          >
            {hasCount ? `History (${count})` : 'History'}
          </Button>
        );
      },
    },
  ];
  const history = useFreeTrialHistory(id, historyPage);
  const historyDataSource = history.data?.data?.map((row: any, index: number) => ({
    ...row,
    key: row.id ?? index,
    no: (historyPage - 1) * HISTORY_PAGE_SIZE + index + 1,
  }));

  const queryClient = useQueryClient();
  const onSaved = () => {
    refetch();
    history.refetch();
    // A save adds a change log entry, so drop any cached change lists
    queryClient.invalidateQueries('free-trial-changes');
  };

  const onPost = useMutation(
    'freeTrial',
    ({ values, id }: { values: string | number; id: string | number }) =>
      users.freeTrial(values, id),
    { onSuccess: onSaved },
  );

  const onPatch = useMutation(
    ({ freeTrialId, quantity }: { freeTrialId: number | string; quantity: number }) =>
      companies.updateFreeTrial(freeTrialId, quantity),
    { onSuccess: onSaved },
  );

  const onFinish = async (values: any) => {
    if (freeTrial?.id) {
      await onPatch.mutate({ freeTrialId: id, quantity: values.quantity });
    } else {
      await onPost.mutate({ values, id });
    }
  };

  const onChangeEndDate = useMutation(
    (endDate: string) => companies.updateFreeTrialEndDate(id, endDate),
    { onSuccess: onSaved },
  );

  const isEndDateUnchanged =
    !newEndDate || (!!freeTrialEndDate && newEndDate.valueOf() === freeTrialEndDate.getTime());

  const isSaving = onPost.isLoading || onPatch.isLoading;

  type FieldType = { limit?: number; quantity?: string };

  return (
    <div className="mainPage">
      <Card loading={isLoading || isFetching} title="Free trial" style={{ width: '100%' }}>

        {/* ── Countdown ── */}
        {isFreeTrialActive && freeTrialEndDate && (
          <Col style={{ marginBottom: 32 }}>
            <Timer
              type="countdown"
              valueStyle={{ fontSize: '32px' }}
              title="Free trial ends"
              value={freeTrialEndDate.getTime()}
              format=" DD : HH : mm : ss "
            />
            <Text type="secondary" style={{ margin: '0 22px 0 6px' }}>Days</Text>
            <Text type="secondary" style={{ margin: '0 10px 0 2px' }}>Hours</Text>
            <Text type="secondary" style={{ margin: '0 5px 0 5px' }}>Minutes</Text>
            <Text type="secondary" style={{ margin: '0 5px 0 5px' }}>Seconds</Text>
          </Col>
        )}

        {/* ── Free trial info ── */}
        {freeTrial && (
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
            <Descriptions.Item label="Status">
              <Tag color={freeTrial.status === 'active' ? 'green' : 'default'}>
                {freeTrial.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Outcome">
              <Tag color={
                freeTrial.outcome === 'converted' ? 'blue'
                : freeTrial.outcome === 'cancelled' ? 'red'
                : 'orange'
              }>
                {freeTrial.outcome ?? '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Days">{freeTrial.days ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Quantity">{freeTrial.quantity ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Start Date">
              {freeTrial.startDate ? moment(freeTrial.startDate).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="End Date">
              {freeTrial.endDate ? moment(freeTrial.endDate).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}

        {/* ── Change end date (Delta ELD only) ── */}
        {canChangeEndDate && (
          <div style={{ marginBottom: 24 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Change end date
            </Text>
            <Space wrap>
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                allowClear={false}
                value={newEndDate}
                onChange={setNewEndDate}
                disabledDate={(current) =>
                  !!current &&
                  (current.isBefore(dayjs(), 'day') ||
                    current.isAfter(dayjs().add(MAX_END_DATE_DAYS, 'day'), 'day'))
                }
                style={{ width: 220 }}
              />
              <Button
                type="primary"
                loading={onChangeEndDate.isLoading}
                disabled={isEndDateUnchanged}
                onClick={() => {
                  if (!newEndDate) return;
                  if (!newEndDate.isAfter(dayjs())) {
                    message.error('End date must be in the future');
                    return;
                  }
                  if (newEndDate.isAfter(dayjs().add(MAX_END_DATE_DAYS, 'day'), 'day')) {
                    message.error(`End date can be at most ${MAX_END_DATE_DAYS} days from today`);
                    return;
                  }
                  onChangeEndDate.mutate(newEndDate.toISOString());
                }}
              >
                Save end date
              </Button>
            </Space>
          </div>
        )}

        {/* ── Form ── */}
        <Form
          form={form}
          layout="vertical"
          style={{ maxWidth: 600 }}
          onFinish={onFinish}
          name="free_trial"
          initialValues={{
            limit: 7,
            quantity: freeTrial?.quantity ?? data?.quantity,
          }}
        >
          {!isActivated && (
            <Form.Item<FieldType>
              label="Limit"
              name="limit"
              rules={[{ required: true, message: 'Please input your limit!' }]}
            >
              <Select style={{ width: '100%' }}>
                {Array.from({ length: maxLimitDays }, (_, i) => (
                  <Select.Option key={i + 1} value={i + 1}>
                    {i + 1} day{i + 1 > 1 ? 's' : ''}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item<FieldType>
            label="Quantity"
            name="quantity"
            rules={[{ required: true, message: 'Please input your quantity!' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          {canSubmit && (
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isSaving}>
                Submit
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>

      <Card title="Free trial history" style={{ width: '100%', marginTop: 16 }}>
        <Table
          dataSource={historyDataSource}
          columns={historyColumnsWithActions}
          loading={history.isLoading || history.isFetching}
          scroll={{ x: 'max-content' }}
          pagination={{
            total: history.data?.count,
            current: historyPage,
            pageSize: HISTORY_PAGE_SIZE,
            showSizeChanger: false,
            onChange: setHistoryPage,
          }}
        />
      </Card>

      <FreeTrialChangesModal
        companyId={id}
        freeTrialId={changesTrialId}
        open={changesTrialId !== undefined}
        onClose={() => setChangesTrialId(undefined)}
      />
    </div>
  );
};

export default FreeTrial;
